import { createContext, useEffect, useState } from 'react'
import { message } from 'antd'
import { useRouter } from 'next/router'
import { INITIAL_SEARCH_STATE } from './constants'
import {
  applyLastSearchTermOnUrl,
  createFiltersUuid,
  fetchFilters,
  fetchJobByJobRefAndUserLocation,
  generateInitialSearchState,
  getRequestBody,
  getSearchStateFromUuid,
  handleCompanyNameParamInUrl,
  handleFilterParamInUrl,
  isReadyToSearch,
  searchJobs,
  statesAreEqual,
} from './helpers'
import { JobSearchStateProps } from './constants'
import _isEmpty from 'lodash-es/isEmpty'
import _isArray from 'lodash-es/isArray'
import _isUndefined from 'lodash-es/isUndefined'
import _isEqual from 'lodash-es/isEqual'
import _differenceWith from 'lodash-es/differenceWith'
import _keys from 'lodash-es/keys'
import _toPairs from 'lodash-es/toPairs'
import _fromPairs from 'lodash-es/fromPairs'
import _values from 'lodash-es/values'
import _omit from 'lodash-es/omit'

import { useQueryParams, StringParam } from 'next-query-params'
import {
  FULL_REVENUE_FILTERS,
  SearchContextProps,
  SHORTENED_REVENUE_FILTERS,
  Stage,
} from './types'
import { useAuth } from 'contexts/AuthContext'
import { stringify } from 'qs'

export const SearchContext = createContext<SearchContextProps>(null)

export const SearchContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Last search
  const LAST_SEARCH_OBJ = JSON.parse(
    localStorage?.getItem('jobSearchState') || '{}'
  )

  // Router
  const router = useRouter()
  const { pathname } = router
  const [query, setQuery] = useQueryParams({
    companyName: StringParam,
    filter: StringParam,
    filterValue: StringParam,
    term: StringParam,
    uuid: StringParam,
    jobId: StringParam,
  })

  // State
  const [stage, setStage] = useState<Stage>(Stage.INITIALIZING)
  const [searchState, setSearchState] = useState<typeof INITIAL_SEARCH_STATE>(
    generateInitialSearchState(query.term, LAST_SEARCH_OBJ)
  )

  // Search Details
  const [searchDetails, setSearchDetails] = useState({
    loading: false,
    overview: null,
    lastFilterAdded: null,
    lastFilterModified: null,
    hasMore: false,
    pageNum: 1,
  })

  const {
    loading,
    overview,
    hasMore,
    pageNum,
    lastFilterAdded,
    lastFilterModified,
  } = searchDetails
  const { userLocation } = useAuth()

  // Filters
  const [filters, setFilters] = useState<{ [x: string]: any[] }>({})
  const [filtersLoading, setFiltersLoading] = useState(true)
  const [customFilters, setCustomFilters] = useState<{ [x: string]: any[] }>({})
  const [searchResults, setSearchResults] = useState([])
  const [hideMobileBar, setHideMobileBar] = useState(false)
  const [preSelectedJob, setPreselectedJob] = useState(null)
  const [numberOfResults, setNumberOfResults] = useState([0])

  const isReadyForSearchStateSideEffects =
    stage === Stage.SETTLED &&
    (pathname === '/search/jobs' || pathname === '/search/all')

  const addCustomFilter = (
    field: string,
    newFilter: { key: string; pretty_name: string; doc_count: number }
  ) => {
    let targetField = field
    if (field === 'location') {
      targetField = 'location_permutations'
    }

    if (field === 'anonymous_apply') {
      targetField = 'is_accepts_anonymous_application'
    }

    if (filters[targetField].find(({ key: _key }) => _key === newFilter.key))
      return
    setFilters((vals) => ({
      ...vals,
      [targetField]: [newFilter, ...vals[targetField]],
    }))
    setSearchDetails((vals) => ({ ...vals, lastFilterAdded: targetField }))
    const newFieldFilters = customFilters[targetField]
      ? [...customFilters[targetField], newFilter]
      : [newFilter]
    setCustomFilters((vals) => ({ ...vals, [targetField]: newFieldFilters }))
  }

  const removeCustomFilter = (field: string, key: string) => {
    let targetField = field
    if (field === 'location') {
      targetField = 'location_permutations'
    }

    if (field === 'anonymous_apply') {
      targetField = 'is_accepts_anonymous_application'
    }
    const target = filters[targetField]?.find?.(({ key: _key }) => _key === key)
    if (!target || _isEmpty(customFilters?.[targetField])) return
    const newFieldFilters = filters[targetField].filter(
      (_filter) => _filter !== target
    )
    setFilters((vals) => ({ ...vals, [targetField]: newFieldFilters }))
    const newCustomFiltersField = customFilters[targetField].filter(
      (_customFilter) => _customFilter !== target
    )
    setCustomFilters((vals) => ({
      ...vals,
      [targetField]: newCustomFiltersField,
    }))
  }

  const removeCustomFiltersInField = (field: string) => {
    let targetField = field
    if (field === 'location') {
      targetField = 'location_permutations'
    }

    if (field === 'anonymous_apply') {
      targetField = 'is_accepts_anonymous_application'
    }
    setCustomFilters((vals) => ({ ...vals, [targetField]: [] }))
  }

  const handleSearch = async (
    rid?: string,
    adminAuthToken?: string,
    searchFilters = true
  ) => {
    const searchStateWithRid: JobSearchStateProps = rid
      ? { ...searchState, userRid: rid }
      : searchState
    if (!_isEmpty(rid) && searchState.userRid !== rid) {
      return setSearchState(searchStateWithRid)
    }

    const changes = _differenceWith(
      _toPairs(LAST_SEARCH_OBJ),
      _toPairs(searchStateWithRid),
      _isEqual
    )

    const changedFilter = _keys(_fromPairs(changes))[0] ?? null

    const isSameFilterTypeChanged =
      !lastFilterModified || changedFilter === lastFilterModified

    setSearchDetails((vals) => ({ ...vals, lastFilterModified: changedFilter }))

    const isSearchTextChanged =
      LAST_SEARCH_OBJ.searchText !== searchStateWithRid.searchText

    if (isSearchTextChanged) setFilters({})

    if (
      !isReadyToSearch(query.term, searchStateWithRid) ||
      !isReadyForSearchStateSideEffects
    )
      return
    const isTheSameSearch = _isEqual(LAST_SEARCH_OBJ, searchStateWithRid)
    // Sets current search state to localStorage and create uuid for last search
    if (!isTheSameSearch) {
      localStorage?.setItem(
        'jobSearchState',
        JSON.stringify(searchStateWithRid)
      )
      createFiltersUuid(setQuery, searchStateWithRid)
    }
    searchJobs(
      setSearchDetails,
      setSearchResults,
      searchStateWithRid,
      adminAuthToken
    )
    if (
      (isSameFilterTypeChanged || _isEmpty(filters) || isSearchTextChanged) &&
      searchFilters
    ) {
      fetchFilters(
        searchStateWithRid,
        customFilters,
        setFiltersLoading,
        setFilters,
        adminAuthToken,
        userLocation
      )
    }
  }

  const removeLastFilterAdded = () => {
    const revenueFilter = SHORTENED_REVENUE_FILTERS.filters.filter((filter) =>
      lastFilterAdded.includes(filter)
    )?.[0]

    if (revenueFilter) {
      const filtersToRemove = FULL_REVENUE_FILTERS.filter((filter) =>
        filter.includes(revenueFilter)
      )
      updateSearchProperty(filtersToRemove[0], null)
      updateSearchProperty(filtersToRemove[1], null)
      return
    }

    updateSearchProperty(lastFilterAdded, '')
  }

  // Returns true if there are more results
  const nextPage = async (adminAuthToken?: string) => {
    const requestBody = getRequestBody(pageNum + 1, searchState)
    try {
      const reqQuery = stringify({ job: requestBody, adminAuthToken })

      const response = await fetch(`/api/search/jobs?${reqQuery}`).then(
        (res) => {
          if (!res.ok) throw res.statusText
          return res.json()
        }
      )

      setSearchResults([...searchResults, ...response.results])
      if (response.filters && _isEmpty(filters)) {
        setFilters({
          ...response.filters,
          posted_at: [...response.filters.posted_at].reverse(),
        })
      }
      setSearchDetails((val) => ({
        ...val,
        overview: response.overview,
        hasMore: !response.overview.last_page,
        pageNum: pageNum + 1,
      }))
    } catch (e) {
      console.error('Could not search jobs: ', e)
      message.error('Could not search jobs.')
    }
  }

  const updateSearchProperty = (
    property: keyof typeof INITIAL_SEARCH_STATE,
    value: any
  ) => {
    setSearchDetails((val) => ({ ...val, lastFilterAdded: property }))
    clearPreSelectedJob()
    setSearchState((val) => ({
      ...val,
      [property]: value,
    }))
  }

  const clearSearchState = () => {
    clearUrlJobId()
    setSearchState({
      ...INITIAL_SEARCH_STATE,
      searchText: searchState.searchText,
      sort: 'relevancy',
    })

    setSearchDetails((val) => ({
      ...val,
      overview: null,
      pageNum: 1,
    }))
  }

  const clearUrlJobId = () => {
    if (query.jobId) {
      setQuery({ jobId: undefined })
    }
  }

  const fetchJobByJobRef = (jobRef) =>
    fetchJobByJobRefAndUserLocation(jobRef, userLocation)

  const setPreselectedJobFromJobRef = async (jobRef: string) => {
    try {
      const job = await fetchJobByJobRef(jobRef)
      if (job) {
        setPreselectedJob(job)
        return job
      }
    } catch (e) {
      console.error('Error on fetching job from jobId', e)
    }
  }

  const clearPreSelectedJob = () => setPreselectedJob(null)

  // initialize onloaded only in /search/jobs page
  useEffect(() => {
    if (pathname !== '/search/jobs') return setStage(Stage.SETTLED)
    const initialize = async () => {
      // Apply search state from uuid only if all params except uuid and jobId is undefined
      if (
        !_isEmpty(query.uuid) &&
        _values(_omit(query, ['uuid', 'jobId'])).every(_isEmpty)
      ) {
        const stateFromUuid = await getSearchStateFromUuid(
          setSearchDetails,
          searchDetails,
          searchState,
          query,
          setQuery
        )
        if (stateFromUuid) {
          setSearchState(stateFromUuid)
          return setStage(Stage.SETTLED)
        }
      }
      setQuery({ uuid: undefined })

      let state = { ...searchState }

      const resetState = () => {
        state = {
          ...INITIAL_SEARCH_STATE,
          searchText: searchState.searchText,
          sort: 'relevancy',
        }
      }

      const updateStateByField = (
        property: keyof typeof INITIAL_SEARCH_STATE,
        value: any
      ) => {
        state = {
          ...state,
          [property]: value,
        }
      }

      if (query.filter && query.filterValue) {
        handleFilterParamInUrl(
          query.filter,
          query.filterValue,
          resetState,
          updateStateByField,
          () => setQuery({ filter: undefined, filterValue: undefined })
        )
      }

      if (query.companyName) {
        handleCompanyNameParamInUrl(
          query.companyName,
          searchState.company_name,
          updateStateByField
        )
      }

      // Apply last search term in url if they are the same and no term param has been set
      applyLastSearchTermOnUrl(
        LAST_SEARCH_OBJ.searchText,
        searchState.searchText,
        query.term,
        setQuery
      )

      // Apply the finalized state to searchState
      setSearchState(state)
      setStage(Stage.SETTLED)
    }
    initialize()
  }, [])

  // Updates filters from url params then deletes those params
  useEffect(() => {
    if (
      !isReadyForSearchStateSideEffects ||
      !(query.filter && query.filterValue)
    )
      return
    handleFilterParamInUrl(
      query.filter,
      query.filterValue,
      clearSearchState,
      updateSearchProperty,
      () => setQuery({ filter: undefined, filterValue: undefined })
    )
  }, [query.filter])

  useEffect(() => {
    if (
      !isReadyForSearchStateSideEffects ||
      !statesAreEqual(LAST_SEARCH_OBJ, searchState)
    ) {
      return
    }
    message.success('Applied search settings from your previous search')
  }, [isReadyForSearchStateSideEffects])

  // Update searchState.searchTerm from term param
  useEffect(() => {
    const bothAreEqual = _isEqual(searchState.searchText, query.term)
    const bothAreEmpty =
      _isEmpty(searchState.searchText) && _isEmpty(query.term)
    if (!isReadyForSearchStateSideEffects || bothAreEqual || bothAreEmpty) {
      return
    }
    setSearchDetails((val) => ({ ...val, lastFilterAdded: 'searchText' }))
    updateSearchProperty('searchText', query.term || '')
  }, [query.term, searchState.searchText])

  // Apply companyName from url to state
  useEffect(() => {
    if (!isReadyForSearchStateSideEffects) {
      return
    }
    if (_keys(searchState.company_name).length > 1) {
      return
    }
    if (
      query.companyName &&
      query.companyName === _keys(searchState.company_name)[0]
    ) {
      return
    }

    if (_isEmpty(query.companyName)) {
      updateSearchProperty('company_name', {})
      return
    }
    setSearchState({
      ...INITIAL_SEARCH_STATE,
      company_name: { [query.companyName]: true },
    })
  }, [query.companyName])

  // When company_name.length is 0 || 1, Set url param according to state
  useEffect(() => {
    if (!isReadyForSearchStateSideEffects) return
    const companyNameFilters = _keys(searchState.company_name)
    if (companyNameFilters.length > 1) {
      setQuery({ companyName: undefined })
      return
    }
    if (
      companyNameFilters.length === 1 &&
      !_isEmpty(companyNameFilters[0]) &&
      companyNameFilters[0] === query.companyName
    ) {
      return
    }
    if (_isEmpty(companyNameFilters[0]) || companyNameFilters.length > 1) {
      setQuery({ companyName: undefined })
      return
    }
    setQuery({ companyName: companyNameFilters[0] })
  }, [searchState.company_name])

  // Avoids having a selected job thats not in the list, and correct total count
  useEffect(() => {
    if (!isReadyForSearchStateSideEffects) return
    let resultsToShow = [...searchResults]
    if (
      preSelectedJob &&
      searchResults[0]?.job_reference_clean !==
        preSelectedJob?.job_reference_clean
    ) {
      const filteredResults = searchResults.filter(
        (result) =>
          result.job_reference_clean !== preSelectedJob?.job_reference_clean
      )
      resultsToShow = [preSelectedJob, ...filteredResults]
      setSearchResults(resultsToShow)
    }

    const isPreSelectedJobIncluded = resultsToShow.includes(preSelectedJob)
    if (
      preSelectedJob &&
      !isPreSelectedJobIncluded &&
      overview?.total_results
    ) {
      setSearchDetails((val) => ({
        ...val,
        overview: (overview) => ({
          ...overview,
          total_results: overview.total_results + 1,
        }),
      }))
    }
  }, [searchResults, preSelectedJob])

  useEffect(() => {
    if (
      overview &&
      !_isUndefined(overview.total_results) &&
      overview?.total_results !== numberOfResults[numberOfResults.length - 1]
    ) {
      setNumberOfResults([...numberOfResults, overview?.total_results])
    }
  }, [overview])

  // Reset after state change
  useEffect(() => {
    if (!isReadyForSearchStateSideEffects) return
    clearPreSelectedJob()
    // clearUrlJobId()
    setSearchResults([])
    setSearchDetails((val) => ({
      ...val,
      pageNum: 1,
      overview: null,
    }))
  }, _values(searchState))

  return (
    <SearchContext.Provider
      value={{
        handleSearch,
        searchState,
        searchResults,
        setSearchState,
        updateSearchProperty,
        clearSearchState,
        filters,
        filtersLoading,
        nextPage,
        hasMore,
        loading,
        overview,
        lastFilterAdded,
        numberOfResults,
        pageNum,
        hideMobileBar,
        setHideMobileBar,
        setPreselectedJobFromJobRef,
        preSelectedJob,
        clearPreSelectedJob,
        searchByCompanyName: (company_name) => {
          clearSearchState()
          router.push(
            `/search/jobs?companyName=${encodeURIComponent(company_name)}`
          )
        },
        addCustomFilter,
        removeCustomFilter,
        removeCustomFiltersInField,
        isReadyForSearchStateSideEffects,
        removeLastFilterAdded,
        fetchJobByJobRef,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}
